import { useMemo } from "react";

import { KpiCard } from "src/components/Analytics/KpiCard/KpiCard";
import { TutorialVideoCard } from "src/components/Analytics/TutorialVideoCard";
import { DataChart } from "src/components/dataView/DataChart/DataChart";
import { DataTable } from "src/components/dataView/DataTable/DataTable";
import {
  colorGradient,
  headerTooltip,
} from "src/components/dataView/DataTable/tableHelpers";
import { MetricsNumberInput } from "src/components/dataView/MetricsTable/MetricsNumberInput";
import DashboardLayout from "src/layouts/DashboardLayout/DashboardLayout";
import { tutorialVideos } from "src/lib/constants";
import { numberFormat } from "src/lib/format";
import { breakEvenAtom, useAnalytics } from "src/lib/hooks/useAnalytics";
import { BREAK_EVEN_ANALYSIS_QUERY } from "src/lib/queries/analytics";

function BreakEvenCell({ column, getValue }) {
  const format = column.columnDef.meta.format;
  return <span className="text-right">{format(getValue())}</span>;
}

const breakEvenChartConfig = {
  netRevenue: {
    type: "area",
    color: "var(--chart-1)",
    label: "Net Revenue",
  },
  profitContribution: {
    type: "area",
    color: "var(--chart-good)",
    label: "Profit Contribution",
  },
  // netProfitMargin: {
  //   type: "line",
  //   yAxis: { orientation: "right", id: "right" },
  //   color: "var(--chart-2)",
  //   label: "Current Net Profit Margin",
  //   shortLabel: "Net Margin",
  // },
  fixedCosts: {
    type: "line",
    color: "var(--chart-bad)",
    label: "Fixed Costs",
  },
};

const breakEvenTooltipContentConfig = {
  labelFormatter: (_, payload) => {
    const orders = payload?.[0]?.payload?.orders;

    return `Order Volume: ${numberFormat.whole(orders, 0, 0)}`;
  },
};

const getInputs = (numberFormat) => [
  {
    id: "orders",
    label: "Order Volume",
    format: numberFormat.whole,
  },
  {
    id: "aov",
    label: "AOV",
    format: numberFormat.currency,
  },
  {
    id: "grossMargin",
    label: "Gross Margin",
    format: numberFormat.percent,
  },
  {
    id: "mer",
    label: "MER",
    format: numberFormat.whole,
  },
  {
    id: "operatingExpense",
    label: "Fixed Cost",
    format: numberFormat.currency,
  },
];

const getColumns = (numberFormat) =>
  [
    {
      accessorKey: "orders",
      header: headerTooltip(
        "Order Volume",
        "The number of orders evaluated at this volume level"
      ),
      meta: {
        accessor: ({ orders }) => orders,
        format: (v) => numberFormat.whole(v, 0, 0),
        className: "text-right",
      },
      cell: ({ row }) => {
        return (
          <div className="flex w-full items-center justify-end gap-2">
            {row.index === 15 && (
              <div className="group-data-[loading=false]:bg-primary size-3 rounded-full" />
            )}
            <span className="text-right">
              {numberFormat.whole(row.original.orders, 0, 0)}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "netRevenue",
      header: headerTooltip(
        "Net Revenue",
        "Total sales revenue after returns and discounts, before any costs are deducted"
      ),
      meta: {
        accessor: ({ aov, orders }) => aov * orders,
        className: "text-right",
      },
      cell: BreakEvenCell,
    },
    {
      accessorKey: "variableCosts",
      header: headerTooltip(
        "Variable Costs",
        "All per-order expenses that scale with volume (e.g. COGS, shipping, transaction fees)"
      ),
      meta: {
        accessor: ({ grossMargin, aov, orders }) =>
          aov * orders * (1 - grossMargin / 100),
        className: "text-right",
      },
      cell: BreakEvenCell,
    },
    {
      accessorKey: "marketingSpend",
      header: headerTooltip(
        "Marketing Spend",
        "The total marketing investment required to generate these orders"
      ),
      meta: {
        accessor: ({ aov, orders, mer }) => (mer ? (aov * orders) / mer : 0),
        className: "text-right",
      },
      cell: BreakEvenCell,
    },
    {
      accessorKey: "profitContribution",
      header: headerTooltip(
        "Profit Contribution",
        "Revenue remaining after subtracting variable costs and marketing spend"
      ),
      meta: {
        accessor: ({ aov, orders, grossMargin, mer }) =>
          aov * orders * (grossMargin / 100) -
          (mer ? aov * orders * (1 / (mer || 1)) : 0),
        className: "text-right",
      },
      cell: BreakEvenCell,
    },
    {
      accessorKey: "operatingExpense",
      header: headerTooltip(
        "Fixed Cost",
        "Overhead expenses that do not change with order count (e.g. rent, salaries, utilities)"
      ),
      meta: {
        accessor: ({ operatingExpense }) => operatingExpense,
        className: "text-right",
      },
      cell: BreakEvenCell,
    },
    {
      accessorKey: "netIncome",
      header: headerTooltip(
        "Net Income",
        "The true bottom-line profit after fixed costs have been covered"
      ),
      meta: {
        accessor: ({ aov, orders, grossMargin, mer, operatingExpense }) =>
          aov * orders * (grossMargin / 100) -
          (mer ? aov * orders * (1 / (mer || 1)) : 0) -
          operatingExpense,
        className: "text-right",
      },
      cell: colorGradient({
        colors: ["var(--chart-bad)", "transparent", "var(--chart-good)"],
        asBadge: true,
        format: numberFormat.currency,
        coerceNonFinite: false,
        domain: ({ table }) => {
          const values = table
            .getRowModel()
            .rows.map((row) => row.getValue("netIncome"));

          return [Math.min(...values, 0), 0, Math.max(...values, 0)];
        },
      }),
    },
  ].map((column) => ({
    ...column,
    meta: { format: numberFormat.currency, ...column.meta },
  }));

const loadingData = Array.from({ length: 31 }).map(() => ({
  orders: "-",
}));

const getDiff = (original, test, format) => {
  if (test === 0 || test == null || original == null) {
    return null;
  }

  const diff = test - original;
  if (diff === 0) {
    return null;
  }

  const percentDiff = (100 * diff) / original;
  return format(diff) + ` (${numberFormat.percentWithSign(percentDiff)})`;
};

export function BreakEvenAnalysis() {
  const analytics = useAnalytics(
    breakEvenAtom,
    BREAK_EVEN_ANALYSIS_QUERY,
    "breakEvenAnalysis",
    (data) => data.data
  );
  const { store, setStore, refreshData, numberFormat } = analytics;
  const inputs = useMemo(() => getInputs(numberFormat), [numberFormat]);
  const columns = useMemo(() => getColumns(numberFormat), [numberFormat]);

  const tableData = useMemo(() => {
    if (!store?.data) return [];

    const testInputs = store.testInputs || {};
    const current = { ...store.data, ...testInputs };
    const data = [];
    for (let i = -15; i <= 15; i++) {
      data.push(
        columns.reduce((acc, { accessorKey, meta }) => {
          const accessor = meta?.accessor;
          if (!accessorKey || !accessor) return acc;

          const data = {
            ...current,
            orders: current.orders + current.orders * 0.05 * i,
          };
          acc[accessorKey] = accessor(data) || 0;

          if (i === 0) {
            acc.meta = { highlight: true };
          }

          return acc;
        }, {})
      );
    }

    return data;
  }, [store.data, store.testInputs, columns]);

  const {
    breakEvenOrders,
    breakEvenMarketing,
    testBreakEvenOrders,
    testBreakEvenMarketing,
  } = useMemo(() => {
    if (!store?.data) return [null, null];

    const current = { ...store.data };
    const testInputs = store.testInputs || {};
    const test = { ...current, ...testInputs };

    const breakEvenOrders = Math.ceil(
      current.operatingExpense /
        (current.aov * (current.grossMargin / 100) -
          (current.mer ? current.aov / current.mer : 0))
    );
    const breakEvenMarketing = Math.ceil(
      current.mer ? (breakEvenOrders * current.aov) / current.mer : 0
    );

    // don't return test if nothing changed
    if (
      test.aov === current.aov &&
      test.grossMargin === current.grossMargin &&
      test.mer === current.mer &&
      test.operatingExpense === current.operatingExpense
    ) {
      return { breakEvenOrders, breakEvenMarketing };
    }

    const testBreakEvenOrders = Math.ceil(
      test.operatingExpense /
        (test.aov * (test.grossMargin / 100) -
          (test.mer ? test.aov / test.mer : 0))
    );
    const testBreakEvenMarketing = Math.ceil(
      test.mer ? (testBreakEvenOrders * test.aov) / test.mer : 0
    );

    return {
      breakEvenOrders,
      breakEvenMarketing,
      testBreakEvenOrders,
      testBreakEvenMarketing,
    };
  }, [store.data, store.testInputs]);

  const chartData = useMemo(() => {
    if (!store?.data) return [];

    const testInputs = store.testInputs || {};
    const current = { ...store.data, ...testInputs };

    const rows = [];
    for (let i = -15; i <= 15; i++) {
      const orders =
        Math.max(0, Math.round(current.orders + current.orders * 0.05 * i)) ||
        0;

      const netRevenue = current.aov * orders;
      const variableCosts =
        current.aov * orders * (1 - current.grossMargin / 100);
      const fixedCosts = current.operatingExpense;
      const marketingSpend = current.mer
        ? (current.aov * orders) / (current.mer || 1)
        : 0;
      const profitContribution = netRevenue - variableCosts - marketingSpend;
      const netIncome = profitContribution - current.operatingExpense;
      const netProfitMargin = (100 * netIncome) / (netRevenue || 1);

      rows.push({
        orders,
        netRevenue,
        variableCosts,
        fixedCosts,
        profitContribution,
        netProfitMargin,
      });
    }
    return rows;
  }, [store.data, store.testInputs]);

  return (
    <>
      <DashboardLayout.Header onRefresh={refreshData} loading={store.loading} />
      <DashboardLayout.Main>
        <DashboardLayout.Filters>
          {inputs.map(({ id, label, format }) => (
            <MetricsNumberInput
              key={id}
              title={`${label}:`}
              defaultValue={store.data?.[id] || 0}
              formatFn={format}
              field={id}
              atom={breakEvenAtom}
              queryVariableMapping={null}
              onSubmit={() => {
                setStore((prev) => ({
                  ...prev,
                  testInputs: {
                    ...prev.testInputs,
                    [id]: Number(prev[id]) || 0,
                  },
                }));
              }}
              disabled={store.loading}
            />
          ))}
        </DashboardLayout.Filters>

        <div className="@container flex flex-col gap-8 @lg:flex-row">
          <DataTable
            className="max-h-[50vh] w-full min-w-0"
            columns={columns}
            data={tableData}
            atom={breakEvenAtom}
            loadingData={loadingData}
            alternateRowColors
          />
          <div className="@container @lg:w-1/2 @2xl:w-1/3">
            <DashboardLayout.Cards>
              <KpiCard
                title="Breakeven Order Volume"
                subtitle="The minimum order volume needed to cover all costs"
                value={testBreakEvenOrders || breakEvenOrders || 0}
                secondary={testBreakEvenOrders && breakEvenOrders}
                secondaryFormat={numberFormat.whole}
                format={numberFormat.whole}
                loading={store.loading}
                diff={getDiff(
                  breakEvenOrders,
                  testBreakEvenOrders,
                  numberFormat.whole
                )}
                diffClassName={
                  testBreakEvenOrders > breakEvenOrders
                    ? "bg-chart-bad"
                    : "bg-chart-good"
                }
              />

              <KpiCard
                title="Breakeven Marketing Spend"
                subtitle="The marketing investment required to reach breakeven volume"
                value={testBreakEvenMarketing || breakEvenMarketing || 0}
                secondary={testBreakEvenMarketing && breakEvenMarketing}
                secondaryFormat={numberFormat.currency}
                format={numberFormat.currency}
                loading={store.loading}
                diff={getDiff(
                  breakEvenMarketing,
                  testBreakEvenMarketing,
                  numberFormat.currency
                )}
                diffClassName={
                  testBreakEvenMarketing > breakEvenMarketing
                    ? "bg-chart-bad"
                    : "bg-chart-good"
                }
              />
            </DashboardLayout.Cards>
          </div>
        </div>

        <DashboardLayout.Cards className="flex flex-col-reverse gap-8 @xl/main:flex-row">
          <DataChart
            title="Profit Breakeven Point"
            data={chartData}
            loading={store.loading}
            xAxisDataKey="orders"
            showYAxis
            className="w-full"
            chartConfig={breakEvenChartConfig}
            referenceLine={breakEvenOrders || undefined}
            valueFormatter={(v) => numberFormat.currency(v)}
            xAxisFormatter={numberFormat.whole}
            tooltipContentConfig={breakEvenTooltipContentConfig}
          />

          <TutorialVideoCard className="w-fit" {...tutorialVideos.breakeven} />
        </DashboardLayout.Cards>
      </DashboardLayout.Main>
    </>
  );
}
